/**
 * One pilot test item (Bill 2026-11-04): what it tests (read-only here; Risk Management changes that in the Risk
 * panel), what the tester updates (status, pass / fail, assignment, due date, test account, notes), the life-ring
 * reports filed from it with their CHG numbers, and every recorded change (who, when, old -> new).
 *
 * Choosing "Issue Identified" opens the life-ring report prefilled with the test; the item becomes Issue Identified
 * once the report is sent (the server records both).
 */
import { useEffect, useMemo, useState } from 'react';
import { ApiError } from '@retail-ops/api-client';
import { Banner, Button, ModalShell, Pill } from '@/components/primitives';
import { useApiClient } from '@/contexts/ApiClientContext';
import type { PilotHistoryRow, PilotItem, PilotItemPatch, PilotSection } from './pilotApi';
import { columnLabel, historyWords, itemHeadline, nameOf, shortDay, statusTone, whenText } from './pilotModel';

export interface PilotItemDialogProps {
  item: PilotItem | null;
  section: PilotSection | undefined;
  statuses: ReadonlyArray<string>;
  /** The issue dialog is open over this one (Escape belongs to it). */
  issueOpen: boolean;
  onClose: () => void;
  onSaved: (item: PilotItem) => void;
  onReportIssue: (item: PilotItem) => void;
  /** Bumped by the tab after a report was tied to this item, to reload its history. */
  refreshKey: number;
}

interface Draft {
  status: string;
  result: string;
  assignedTo: string;
  dueDate: string;
  evidenceRef: string;
  notes: string;
}

function draftOf(i: PilotItem): Draft {
  return {
    status: i.status ?? '',
    result: i.result ?? '',
    assignedTo: i.assignedTo ?? '',
    dueDate: i.dueDate ?? '',
    evidenceRef: i.evidenceRef ?? '',
    notes: i.notes ?? '',
  };
}

export function PilotItemDialog({ item, section, statuses, issueOpen, onClose, onSaved, onReportIssue, refreshKey }: PilotItemDialogProps): JSX.Element | null {
  const client = useApiClient();
  const [draft, setDraft] = useState<Draft | null>(null);
  const [history, setHistory] = useState<PilotHistoryRow[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!item) return;
    setDraft(draftOf(item));
    setErr(null);
    // Reset only when a different item opens or after a save (version changes), not on every board refresh.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item?.id, item?.version]);

  useEffect(() => {
    if (!item) return;
    let cancelled = false;
    setHistory(null);
    void client.http.get<{ history: PilotHistoryRow[] }>(`/v3/rop/pilot/items/${item.id}/history`)
      .then((r) => { if (!cancelled) setHistory(r.history); })
      .catch(() => { if (!cancelled) setHistory([]); });
    return () => { cancelled = true; };
  }, [client, item?.id, item?.version, refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const dirty = useMemo(() => {
    if (!item || !draft) return false;
    const d = draftOf(item);
    return (Object.keys(d) as Array<keyof Draft>).some((k) => d[k] !== draft[k]);
  }, [item, draft]);

  if (!item || !draft) return null;
  const scenario = section?.kind === 'scenarios' || section?.kind === 'results';
  const set = (k: keyof Draft, v: string): void => setDraft((d) => (d ? { ...d, [k]: v } : d));

  async function save(extra: Partial<PilotItemPatch> = {}): Promise<void> {
    if (!item || !draft) return;
    const base = draftOf(item);
    const patch: PilotItemPatch = { expectedVersion: item.version || undefined, ...extra };
    if (draft.status !== base.status) patch.status = draft.status;
    if (draft.result !== base.result) patch.result = draft.result;
    if (draft.assignedTo !== base.assignedTo && !extra.assignToMe) patch.assignedTo = draft.assignedTo;
    if (draft.dueDate !== base.dueDate) patch.dueDate = draft.dueDate;
    if (draft.evidenceRef !== base.evidenceRef) patch.evidenceRef = draft.evidenceRef;
    if (draft.notes !== base.notes) patch.notes = draft.notes;
    setBusy(true);
    setErr(null);
    try {
      const r = await client.http.patch<{ item: PilotItem }>(`/v3/rop/pilot/items/${item.id}`, { body: patch });
      onSaved(r.item);
    } catch (e: unknown) {
      setErr(ApiError.isApiError(e) ? e.message : e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  const onStatus = (v: string): void => {
    // Bill: "Issue Identified" opens the life-ring report prefilled with the test; the report sets the status.
    if (v === 'Issue Identified' && item.status !== 'Issue Identified') { onReportIssue(item); return; }
    set('status', v);
  };

  const kv: Array<[string, string | null]> = [
    [columnLabel(section, 'area', 'Area'), item.area],
    [columnLabel(section, 'title', scenario ? 'Scenario' : 'Item'), item.title],
    [columnLabel(section, 'steps', 'Steps and expected result'), item.steps],
    [columnLabel(section, 'channel', 'Channel'), item.channel],
    [columnLabel(section, 'testerGroup', 'Tester group'), item.testerGroup],
    [columnLabel(section, 'accountTypes', 'Account types'), item.accountTypes],
    [columnLabel(section, 'riskRole', 'Risk Management role'), item.riskRole],
    ...item.details.map((d) => [d.label, d.value] as [string, string | null]),
  ];

  return (
    <ModalShell
      open
      onClose={onClose}
      size="lg"
      title={`${item.code ?? '(no ID)'} · ${itemHeadline(item, section)}`}
      disableEscapeClose={issueOpen}
      disableBackdropClose={issueOpen}
      confirmClose
      isDirty={dirty}
      footer={(
        <div className="pilot-actions">
          {err && <span className="pilot-muted" role="alert" style={{ color: 'var(--err-600)' }}>{err}</span>}
          <Button variant="ghost" onClick={() => onReportIssue(item)} disabled={busy}>Report an issue with this test</Button>
          <Button variant="ghost" onClick={onClose} disabled={busy}>Close</Button>
          <Button onClick={() => void save()} disabled={busy || !dirty}>{busy ? 'Saving…' : 'Save'}</Button>
        </div>
      )}
    >
      <p className="pilot-muted" style={{ marginTop: 0 }}>
        {section?.name}{item.group ? ` › ${item.group}` : ''}
        {section?.phaseNote ? ` · ${section.phaseNote}` : ''}
      </p>
      <dl className="pilot-kv">
        {kv.filter(([, v]) => v != null && v.trim() !== '').map(([k, v]) => (
          <div key={k} style={{ display: 'contents' }}><dt>{k}</dt><dd>{v}</dd></div>
        ))}
      </dl>

      {item.issues.length > 0 && (
        <Banner tone="warn" title="Issues reported from this test">
          {item.issues.map((x) => (
            <div key={x.reportId}>
              {x.changeKey ?? 'Report sent; change number to follow'} · {shortDay(x.reportedAt)} · {nameOf(x.reportedBy)}
            </div>
          ))}
        </Banner>
      )}

      <div className="pilot-form">
        <label className="pilot-field">
          Status
          <select value={draft.status} onChange={(e) => onStatus(e.target.value)}>
            {!draft.status && <option value="">No status yet</option>}
            {statuses.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
        {scenario && (
          <label className="pilot-field">
            Pass / fail
            <select value={draft.result} onChange={(e) => set('result', e.target.value)}>
              <option value="">Not recorded</option>
              <option value="Pass">Pass</option>
              <option value="Fail">Fail</option>
            </select>
          </label>
        )}
        <label className="pilot-field">
          {columnLabel(section, 'assignedTo', 'Assigned to')}
          <input value={draft.assignedTo} maxLength={200} onChange={(e) => set('assignedTo', e.target.value)} />
        </label>
        <div className="pilot-field" style={{ justifyContent: 'flex-end' }}>
          <Button variant="ghost" size="sm" disabled={busy} onClick={() => void save({ assignToMe: true })}>Assign to me</Button>
        </div>
        <label className="pilot-field">
          {columnLabel(section, 'dueDate', 'Due date')}
          <input type="date" value={draft.dueDate} onChange={(e) => set('dueDate', e.target.value)} />
        </label>
        <label className="pilot-field">
          {columnLabel(section, 'evidenceRef', 'Evidence / test account')}
          <input value={draft.evidenceRef} maxLength={1000} placeholder="Test account or CIF (test identities only)"
            onChange={(e) => set('evidenceRef', e.target.value)} />
        </label>
        <label className="pilot-field pilot-field--wide">
          {columnLabel(section, 'notes', 'Notes')}
          <textarea value={draft.notes} maxLength={8000} onChange={(e) => set('notes', e.target.value)} />
        </label>
      </div>

      <h3 className="pilot-h2">Changes</h3>
      {history === null ? <p className="pilot-muted">Loading…</p> : history.length === 0 ? <p className="pilot-muted">No changes recorded.</p> : (
        <ul className="pilot-history">
          {history.map((h) => (
            <li key={h.id}>
              {historyWords(h)}
              <div className="pilot-history__who">{whenText(h.at)} · {nameOf(h.byUpn, h.byName)}{h.via === 'risk' ? ' (Risk panel)' : ''}</div>
            </li>
          ))}
        </ul>
      )}
      <p className="pilot-muted">
        Status now: <Pill variant={statusTone(item.status)}>{item.status ?? 'No status yet'}</Pill>
        {item.result && <> <Pill variant={item.result === 'Fail' ? 'err' : 'ok'}>{item.result}</Pill></>}
      </p>
    </ModalShell>
  );
}
